import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plane, X, LogOut, Circle } from 'lucide-react';
import useGlobale from '../../components/global';

const SidebarAdmin = ({ isOpen, menuItems, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { authenticated, user, logout } = useGlobale();

    return (
        <>
            {/* Overlay for mobile */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm lg:hidden z-40"
                    onClick={onClose}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
                    fixed lg:static inset-y-0 left-0 
                    w-[280px] lg:w-72
                    bg-gradient-to-b from-white to-brand-50
                    shadow-lg 
                    transform transition-transform duration-300 ease-in-out
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                    lg:translate-x-0 
                    z-50
                    flex flex-col
                    h-screen lg:h-auto
                    overflow-y-auto
                    border-r-4 border-brand-600
                `}
            >
                {/* Mobile close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 lg:hidden p-2 rounded-lg hover:bg-green-50 text-green-700"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Logo Section - Orange */}
                <div className="bg-brand-600 p-6">
                    <div className="flex items-center space-x-3">
                        <div className="bg-white p-2 rounded-lg">
                            <Plane className="w-8 h-8 text-brand-600" />
                        </div>
                        <div>
                            <span className="text-2xl font-bold text-white">NigerVoyage</span>
                            <p className="text-xs text-brand-100">Administration</p>
                        </div>
                    </div>
                </div>

                {/* Profile Section - Green */}
                <div className="bg-green-700 p-6">
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <div className="p-1 bg-white rounded-full">
                                <img
                                    src="images/userp.jpg"
                                    alt="Profile"
                                    className="w-14 h-14 rounded-full border-2 border-brand-600 object-cover"
                                />
                            </div>
                            <div className="absolute bottom-0 right-0 w-4 h-4 bg-brand-600 rounded-full border-2 border-white"></div>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white">{user.first_name} {user.last_name}</h3>
                            <div className="flex items-center space-x-2 mt-1">
                                <Circle className="w-2 h-2 fill-brand-600 text-brand-600" />
                                <p className="text-sm text-brand-200">En ligne</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-6 space-y-2">
                    {menuItems.map((item, index) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <button
                                key={index}
                                onClick={() => {
                                    navigate(item.path);
                                    if (window.innerWidth < 1024) {
                                        onClose();
                                    }
                                }}
                                className={`
                                    flex items-center w-full
                                    px-4 py-3
                                    text-sm font-medium rounded-xl
                                    transition-all duration-200
                                    ${isActive
                                        ? 'text-white bg-green-700 shadow-lg shadow-green-700/30'
                                        : 'text-gray-700 hover:bg-brand-100 hover:text-brand-700'
                                    }
                                    focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
                                `}
                            >
                                <div className={`
                                    p-2 rounded-lg mr-3
                                    ${isActive ? 'bg-green-600' : 'bg-white shadow-sm'}
                                `}>
                                    {React.cloneElement(item.icon, {
                                        className: `w-5 h-5 ${isActive ? 'text-white' : 'text-brand-600'}`
                                    })}
                                </div>
                                <span className="font-semibold">{item.label}</span>
                            </button>
                        );
                    })}
                </nav>

                {/* Déconnexion */}
                <div className="p-6 border-t border-brand-200">
                    <button
                        onClick={logout}
                        className="
                            flex items-center w-full
                            px-4 py-3
                            text-sm font-semibold text-white
                            bg-red-500 hover:bg-red-600
                            rounded-xl shadow-lg shadow-red-500/30
                            transition-all duration-200
                            focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
                        "
                    >
                        <div className="p-2 bg-red-400 rounded-lg mr-3">
                            <LogOut className="w-5 h-5" />
                        </div>
                        <span>Déconnexion</span>
                    </button>
                </div>
            </aside>
        </>
    );
};

export default SidebarAdmin;